import React from "react";
import {
  Box,
  FormControlLabelProps,
  styled,
  ToggleButtonGroupProps,
} from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import InfoIcon from "@icons/myicons/info.svg?react";
import { TypographyWithInfoIcon } from "@components";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    width: "73px",
  },
}));

interface Props {
  name: string;
  label: FormControlLabelProps["label"];
  testId?: string;
  tooltip?: {
    title?: string;
    text?: string;
  };
}

type FormInputProps<T extends FieldValues> = ToggleButtonGroupProps &
  Props &
  UseControllerProps<T>;

export const RHFSwitch = <T extends FieldValues>(
  props: FormInputProps<T>
): React.ReactNode => {
  const { t } = useTranslation();

  const { control, name, defaultValue, label, tooltip, testId } = props;

  const { field } = useController({ control, name, defaultValue });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      gap={3}
    >
      {typeof label === "string" ? (
        <TypographyWithInfoIcon
          variant="body1"
          tooltipTitle={tooltip?.title}
          tooltipText={tooltip?.text}
          icon={<InfoIcon />}
          hideIcon={!tooltip?.title && !tooltip?.text}
        >
          {label}
        </TypographyWithInfoIcon>
      ) : (
        label
      )}
      <StyledToggleButtonGroup
        size="small"
        value={field.value === true ? "yes" : "no"}
        aria-label="text formatting"
        data-testid={testId}
      >
        <ToggleButton
          value="yes"
          aria-label="yes"
          onClick={() => field.onChange(true)}
        >
          {t("form.yes")}
        </ToggleButton>
        <ToggleButton
          value="no"
          aria-label="no"
          onClick={() => field.onChange(false)}
        >
          {t("form.no")}
        </ToggleButton>
      </StyledToggleButtonGroup>
    </Box>
  );
};
